import { useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Link,
  Select,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { usePartyNames } from "../../hooks/usePartyNames";
import { recentActivity } from "./placingBook";
import PortalStageBlocks, { STAGE_BLOCKS, brokerStageBlocks } from "./PortalStageBlocks";
import { isNewSince, useSeenStages } from "../../hooks/useSeenStages";
import { DueDate, MarketBar, NextChip, StageDots, relativeTime } from "./PlacingSummaryParts";


/** Every placement the broker's organisations are running, across all deals. */
const BrokerPlacingPortal = ({ summaries, dealsById, productNames }) => {
  const navigate = useNavigate();
  const [stage, setStage] = useState(null); // a stage key, or null for every stage
  const [needsOnly, setNeedsOnly] = useState(true);
  const [search, setSearch] = useState("");
  const [product, setProduct] = useState("");
  const { seenAt, sinceLastVisit, markSeen } = useSeenStages("broker");

  const dealName = (s) => dealsById[s.placement.dealId]?.name;
  const products = [...new Set(summaries.map((s) => s.placement.productCode).filter(Boolean))].sort();
  // New: a carrier has done something since this stage was last opened
  const isNew = (s) => isNewSince(sinceLastVisit, s.stageKey, s.carrierUpdated);

  const blocks = brokerStageBlocks(summaries, seenAt);

  const selectStage = (key) => {
    setStage(key);
    // A stage shows everything in it; back to all stages shows what needs you
    setNeedsOnly(!key);
    if (key) markSeen(key);
  };

  const rows = useMemo(() => {
    const text = search.trim().toLowerCase();
    return summaries
      .filter((s) => !stage || s.stageKey === stage)
      .filter((s) => !needsOnly || s.needsYou || isNew(s))
      .filter((s) => !product || s.placement.productCode === product)
      .filter((s) => !text || [s.placement.name, dealName(s)].some((v) => (v || "").toLowerCase().includes(text)))
      .sort((a, b) => Number(b.needsYou) - Number(a.needsYou) || new Date(b.updated || 0) - new Date(a.updated || 0));
    // dealName and isNew read dealsById and the last visit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaries, stage, needsOnly, search, product, dealsById]);

  const activity = recentActivity(summaries, 5);
  const activityNames = usePartyNames(activity.map((e) => e.partyId));

  return (
    <VStack align="stretch" spacing={5}>
      <HStack justify="space-between" align="flex-end">
        <Box>
          <Heading size="lg">Placing</Heading>
          <Text color="gray.600">Every placement your organisation is running, across all deals</Text>
        </Box>
        <Tooltip label="Open a document and choose Next step → Create submission" hasArrow>
          <Button as={RouterLink} to="/studio" colorScheme="blue" size="sm">+ New placement</Button>
        </Tooltip>
      </HStack>

      <PortalStageBlocks blocks={blocks} selected={stage} onSelect={selectStage} />

      <Box borderWidth="1px" borderRadius="md" bg="whiteAlpha.800">
        <HStack px={4} py={2} justify="space-between" flexWrap="wrap" rowGap={2}>
          <HStack spacing={4}>
            <Heading size="sm">{stage ? STAGE_BLOCKS.find((b) => b.key === stage).title : "All stages"}</Heading>
            <FormControl display="flex" alignItems="center" w="auto">
              <Switch id="broker-needs-only" size="sm" colorScheme="orange" isChecked={needsOnly} onChange={(e) => setNeedsOnly(e.target.checked)} />
              <FormLabel htmlFor="broker-needs-only" mb={0} ml={2} fontSize="sm">Needs you or new only</FormLabel>
            </FormControl>
          </HStack>
          <HStack spacing={2}>
            <Input size="sm" w="240px" placeholder="Search placement or deal…" value={search} onChange={(e) => setSearch(e.target.value)} />
            <Select size="sm" w="150px" value={product} onChange={(e) => setProduct(e.target.value)}>
              <option value="">All products</option>
              {products.map((p) => <option key={p} value={p}>{productNames[p] || p}</option>)}
            </Select>
          </HStack>
        </HStack>

        {rows.length === 0 ? (
          <Text p={6} fontSize="sm" color="gray.500">
            {needsOnly ? "Nothing needs you. Click a stage above to see its placements." : "Nothing here."}
          </Text>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr><Th>Placement</Th><Th>Product</Th><Th>Stage</Th><Th>Market</Th><Th>Next for you</Th><Th>Due</Th><Th>Updated</Th></Tr>
            </Thead>
            <Tbody>
              {rows.map((s) => {
                const p = s.placement;
                return (
                  <Tr
                    key={p._id}
                    bg={s.needsYou ? "orange.50" : undefined}
                    cursor="pointer"
                    _hover={{ bg: s.needsYou ? "orange.100" : "gray.50" }}
                    onClick={() => navigate(`/placing/placements/${p._id}`)}>
                    <Td>
                      <HStack spacing={2}>
                        <Text fontWeight="semibold">{p.name || "Placement"}</Text>
                        {isNew(s) && <Badge colorScheme="red" variant="solid" borderRadius="full" fontSize="0.6em">NEW</Badge>}
                      </HStack>
                      {p.dealId && (
                        <Text fontSize="xs" color="gray.500" onClick={(e) => e.stopPropagation()}>
                          Deal ·{" "}
                          <Link as={RouterLink} color="blue.600" to={`/product/${p.productCode}/deal/${p.dealId}/deal`}>
                            {dealName(s) || "open deal"}
                          </Link>
                        </Text>
                      )}
                    </Td>
                    <Td>{productNames[p.productCode] || p.productCode || "—"}</Td>
                    <Td><StageDots summary={s} /></Td>
                    <Td><MarketBar market={s.market} /></Td>
                    <Td><NextChip next={s.next} /></Td>
                    <Td><DueDate value={s.due} /></Td>
                    <Td color="gray.600">{relativeTime(s.updated)}</Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </Box>

      <Box borderWidth="1px" borderRadius="md" bg="whiteAlpha.800" px={4} py={3}>
        <Heading size="sm" mb={2}>Recent activity</Heading>
        {activity.length === 0 ? (
          <Text fontSize="sm" color="gray.500">Nothing yet.</Text>
        ) : (
          <VStack align="stretch" spacing={0} divider={<Box borderBottomWidth="1px" />}>
            {activity.map((e, i) => (
              <HStack key={i} py={1} fontSize="xs" spacing={4} align="start">
                <Text color="gray.500" w="70px" flexShrink={0}>{relativeTime(e.date)}</Text>
                <Text>{e.describe(activityNames[e.partyId] || "A carrier")}</Text>
              </HStack>
            ))}
          </VStack>
        )}
      </Box>
    </VStack>
  );
};

export default BrokerPlacingPortal;
